/**
 * VulnerabilitiesDashboard — cuadro de mando de seguridad del parque.
 *
 * POR QUÉ EXISTE
 * ---------------------------------------------------------------------------
 * El panel de siempre es una lista honesta pero no deja operar: dice «2.644
 * abiertas» y a partir de ahí hay que cavar. Esto pone delante lo que se puede
 * DECIDIR: cuánto es crítico, qué se está explotando (KEV), qué producto lo
 * arregla y qué equipos están peor.
 *
 * Sin librerías de gráficas: los anillos y las barras son SVG/CSS. Una
 * dependencia menos que mantener y cero problemas con los skins.
 *
 * Los datos vienen de `GET /api/vulnerabilities/overview` (una llamada).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
  FireIcon,
  ServerStackIcon,
  ShieldExclamationIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { KpiCard, OPS_COLORS } from './common/OpsKit';

const SEV = [
  { key: 'critical', label: 'Críticas', color: '#dc2626' },
  { key: 'high', label: 'Altas', color: '#f97316' },
  { key: 'medium', label: 'Medias', color: '#eab308' },
  { key: 'low', label: 'Bajas', color: '#0ea5e9' },
];

const card = {
  border: '1px solid var(--border)',
  borderRadius: 14,
  background: 'var(--bg-card)',
  padding: '14px 16px',
};

/** Anillo de severidad: radio fijo, arcos proporcionales con dasharray. */
function SeverityDoughnut({ bySeverity, total }) {
  const size = 168;
  const stroke = 18;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  const segmentos = SEV
    .map((s) => ({ ...s, value: Number(bySeverity?.[s.key]) || 0 }))
    .filter((s) => s.value > 0);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', width: size, height: size, flex: '0 0 auto' }}>
        <svg width={size} height={size} role="img" aria-label="Hallazgos por severidad">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="var(--border)"
            strokeWidth={stroke}
          />
          {segmentos.map((s) => {
            const frac = s.value / Math.max(total || 1, 1);
            const dash = frac * circumference;
            const el = (
              <circle
                key={s.key}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={s.color}
                strokeWidth={stroke}
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset}
                strokeLinecap="butt"
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              />
            );
            offset += dash;
            return el;
          })}
        </svg>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <span style={{ fontSize: 26, fontWeight: 800, lineHeight: 1 }}>{total}</span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: 1 }}>
            ABIERTAS
          </span>
        </div>
      </div>

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
        {SEV.map((s) => {
          const value = Number(bySeverity?.[s.key]) || 0;
          const pct = total ? Math.round((value / total) * 100) : 0;
          return (
            <li key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color }} />
              <span style={{ minWidth: 62, color: 'var(--text-secondary)' }}>{s.label}</span>
              <strong style={{ minWidth: 52, textAlign: 'right' }}>{value}</strong>
              <span style={{ color: 'var(--text-muted)', minWidth: 38, textAlign: 'right' }}>
                {pct}%
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Barra horizontal de un producto: críticas en rojo, resto en gris. */
function ProductBar({ criticas, total }) {
  const pctCrit = total ? (criticas / total) * 100 : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div
        style={{
          flex: 1,
          height: 8,
          borderRadius: 6,
          background: 'var(--border)',
          overflow: 'hidden',
          minWidth: 80,
        }}
      >
        <div style={{ width: `${pctCrit}%`, height: '100%', background: '#dc2626' }} />
      </div>
      <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 84, textAlign: 'right' }}>
        {criticas} de {total} crít.
      </span>
    </div>
  );
}

export default function VulnerabilitiesDashboard({ companyId, ciType = 'workstation' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      setData(await api.getVulnerabilitiesOverview(companyId, { ci_type: ciType }));
    } catch (e) {
      setError(e?.message || 'No se pudo cargar el resumen de vulnerabilidades');
    } finally {
      setLoading(false);
    }
  }, [companyId, ciType]);

  useEffect(() => { load(); }, [load]);

  const total = Number(data?.total) || 0;
  const bySev = data?.by_severity || {};
  const productos = data?.products || [];
  const equipos = data?.top_ci || data?.equipment || [];

  const kpis = useMemo(() => ([
    { label: 'Hallazgos abiertos', value: total, color: OPS_COLORS.info, icon: ShieldExclamationIcon, hint: 'CVEs sin cerrar' },
    { label: 'Críticas', value: Number(bySev.critical) || 0, color: OPS_COLORS.bad, icon: ExclamationTriangleIcon, hint: 'severidad crítica' },
    { label: 'Explotadas (KEV)', value: Number(data?.kev) || 0, color: '#b91c1c', icon: FireIcon, hint: 'CISA confirma explotación' },
    { label: 'Equipos afectados', value: Number(data?.affected_ci) || 0, color: '#f97316', icon: ServerStackIcon, hint: 'con algún hallazgo abierto' },
    { label: 'Productos', value: productos.length, color: OPS_COLORS.muted, icon: Squares2X2Icon, hint: 'distintos afectados' },
  ]), [total, bySev, data, productos.length]);

  if (loading) return <p className="sf2-hint">Cargando el cuadro de seguridad…</p>;
  if (error) return <p className="sf2-pill sf2-pill--bad">{error}</p>;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {/* ── Cabecera + KPIs ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <ShieldExclamationIcon style={{ width: 22, height: 22, color: '#dc2626' }} />
        <h2 style={{ margin: 0, fontSize: 18 }}>Seguridad del parque</h2>
        <span className="sf2-hint" style={{ margin: 0 }}>
          Software instalado cruzado con NVD y con lo que CISA confirma que se explota.
        </span>
        <button type="button" className="list-btn-secondary" onClick={load} style={{ marginLeft: 'auto' }}>
          <ArrowPathIcon style={{ width: 15, height: 15 }} /> Recalcular
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
        {kpis.map((k) => (
          <KpiCard
            key={k.label}
            icon={k.icon}
            color={k.color}
            label={k.label}
            value={k.value}
            hint={k.hint}
          />
        ))}
      </div>

      {/* ── Anillo + productos ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 1fr) minmax(320px, 1.4fr)', gap: 12 }}>
        <div style={card}>
          <h3 style={{ margin: '0 0 10px', textAlign: 'left', fontSize: 15 }}>Reparto por severidad</h3>
          <SeverityDoughnut bySeverity={bySev} total={total} />
        </div>

        <div style={card}>
          <h3 style={{ margin: '0 0 4px', textAlign: 'left', fontSize: 15 }}>
            Productos que más pesan
          </h3>
          <p className="sf2-hint" style={{ marginTop: 0 }}>
            Casi siempre es uno: una versión vieja. Actualizar un producto cierra cientos de hallazgos.
          </p>
          <div style={{ display: 'grid', gap: 8 }}>
            {productos.slice(0, 8).map((p) => (
              <div
                key={p.name}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(120px, 1fr) auto',
                  gap: 10,
                  alignItems: 'center',
                  borderBottom: '1px solid var(--border)',
                  paddingBottom: 6,
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {p.name || '—'}
                    {Number(p.kev) > 0 && (
                      <span className="sf2-pill sf2-pill--bad" style={{ marginLeft: 6, fontSize: 10 }}>
                        <FireIcon style={{ width: 10, height: 10, marginRight: 3 }} />
                        {p.kev} KEV
                      </span>
                    )}
                  </div>
                  <ProductBar criticas={Number(p.critical) || 0} total={Number(p.cves) || 0} />
                </div>
                <button
                  type="button"
                  className="list-btn-secondary"
                  title="Ir al catálogo de software para desplegar la versión nueva"
                  onClick={() => navigate('/infrastructure/workstations/software')}
                >
                  Actualizar
                  <ArrowTopRightOnSquareIcon style={{ width: 13, height: 13, marginLeft: 4 }} />
                </button>
              </div>
            ))}
            {productos.length === 0 && (
              <p className="sf2-hint" style={{ margin: 0 }}>
                Sin hallazgos abiertos para este parque.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── Equipos peor parados ── */}
      {equipos.length > 0 && (
        <div style={card}>
          <h3 style={{ margin: '0 0 8px', textAlign: 'left', fontSize: 15 }}>Equipos peor parados</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 10 }}>
            {equipos.slice(0, 8).map((e) => (
              <div
                key={`${e.ci_type}-${e.ci_id}`}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  padding: '10px 12px',
                  background: Number(e.kev) > 0 ? 'rgba(220,38,38,0.06)' : 'transparent',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 13 }}>{e.label || e.name || `#${e.ci_id}`}</div>
                <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 12 }}>
                  <span><strong>{e.cves}</strong> <span style={{ color: 'var(--text-muted)' }}>CVEs</span></span>
                  <span style={{ color: '#dc2626' }}><strong>{e.critical}</strong> crít.</span>
                  {Number(e.kev) > 0 && (
                    <span style={{ color: '#b91c1c' }}><strong>{e.kev}</strong> KEV</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
